import type { KeyboardEvent, MouseEvent, ReactNode } from 'react';
import { cn } from '../../lib/cn.ts';
import { isPlainClick } from '../../lib/isPlainClick.ts';
import { alignClass } from './column-align.ts';
import type { DataTableColumn } from './types.ts';

const ROW_LINK = 'data-table-row-link';
/* Anything that handles its own click: a row click must not steal it. */
const INTERACTIVE =
  'a, button, input, select, textarea, label, [role="menuitem"], [role="button"]';

function defaultCell<T>(row: T, key: string): ReactNode {
  const value = (row as unknown as Record<string, unknown>)[key];

  return typeof value === 'string' || typeof value === 'number' ? value : null;
}

interface TableRowProps<T> {
  row: T;
  columns: DataTableColumn<T>[];
  rowHref?: (row: T) => string | undefined;
  onNavigate?: (href: string) => void;
  onRowClick?: (row: T) => void;
  selected: boolean;
  actions?: (row: T) => ReactNode;
  zebra: boolean;
}

/** One record. Its first cell is the row's link when it has somewhere to go. */
export function TableRow<T>({
  row,
  columns,
  rowHref,
  onNavigate,
  onRowClick,
  selected,
  actions,
  zebra,
}: TableRowProps<T>) {
  const href = rowHref?.(row);
  const navigable = href !== undefined || onRowClick !== undefined;
  /* A row with a link is reached through the link; one without is its own stop. */
  const focusable = href === undefined && onRowClick !== undefined;

  function handleClick(event: MouseEvent<HTMLTableRowElement>) {
    /* The link handles its own clicks, and so does every other control in the row. */
    if ((event.target as HTMLElement).closest(INTERACTIVE) || !isPlainClick(event)) {
      return;
    }
    if (href !== undefined) {
      if (onNavigate) {
        event.preventDefault();
        onNavigate(href);
      } else {
        event.currentTarget
          .querySelector<HTMLAnchorElement>(`[data-slot="${ROW_LINK}"]`)
          ?.click();
      }
      return;
    }
    onRowClick?.(row);
  }

  /* Only the row itself: Enter on a control inside it is that control's. */
  function handleKeyDown(event: KeyboardEvent<HTMLTableRowElement>) {
    if (event.target !== event.currentTarget) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    onRowClick?.(row);
  }

  return (
    <tr
      data-slot='data-table-row'
      data-selected={selected || undefined}
      aria-current={selected ? true : undefined}
      tabIndex={focusable ? 0 : undefined}
      onClick={navigable ? handleClick : undefined}
      onKeyDown={focusable ? handleKeyDown : undefined}
      className={cn(
        'border-t border-line transition-colors',
        zebra && 'even:bg-fill-faint',
        navigable && 'cursor-pointer hover:bg-fill-faint',
        focusable &&
          'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ink-faint',
        selected && 'bg-fill hover:bg-fill',
      )}
    >
      {columns.map((column, index) => (
        <td
          key={column.key}
          className={cn(
            'px-3.5 py-3 text-ink-body',
            alignClass[column.align ?? 'start'],
            column.width,
            column.nowrap && 'whitespace-nowrap',
            column.hideBelow === 'mobile' && 'max-mobile:hidden',
          )}
        >
          <CellContent
            content={column.render ? column.render(row) : defaultCell(row, column.key)}
            href={index === 0 ? href : undefined}
            onNavigate={onNavigate}
          />
          {column.mobileSubline && (
            <span className='hidden text-xs font-normal wrap-anywhere text-ink-muted max-mobile:block'>
              {column.mobileSubline(row)}
            </span>
          )}
        </td>
      ))}
      {actions && (
        <td className='px-3.5 py-1.5 text-right whitespace-nowrap'>{actions(row)}</td>
      )}
    </tr>
  );
}

interface CellContentProps {
  content: ReactNode;
  /** Set on the row's first cell only, which then becomes the row's link. */
  href: string | undefined;
  onNavigate?: (href: string) => void;
}

function CellContent({ content, href, onNavigate }: CellContentProps) {
  if (href === undefined) {
    return content;
  }

  return (
    <a
      data-slot={ROW_LINK}
      href={href}
      onClick={
        onNavigate
          ? (event) => {
              if (isPlainClick(event)) {
                event.preventDefault();
                onNavigate(href);
              }
            }
          : undefined
      }
      className='font-bold text-ink hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-faint'
    >
      {content}
    </a>
  );
}
